import { useMemo } from "react";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, AutoComplete, Button, DatePicker, Drawer, Form, Input, Select, Space, Typography } from "antd";

import { type AppRelease } from "@fitness-app/data-models";

import { useAppReleases } from "~/modules/Support/hooks/useAppReleases";

import {
  HELP_CATEGORIES,
  HELP_CHANGE_TYPES,
  HELP_CLIENT_PLATFORMS,
  HELP_PLATFORMS,
  type HelpArticleKind,
} from "@fitness-app/data-models/entities/HelpArticle";

const toOptions = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const UPDATE_TYPE_OPTIONS = [
  { value: "ota", label: "Aktualizacja bez pobierania ze sklepu" },
  { value: "store", label: "Wydanie w App Store / Google Play" },
  { value: "web", label: "Odświeżenie strony w przeglądarce" },
];

const PLATFORM_OPTIONS = [
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
  { value: "web", label: "Przeglądarka" },
];

/**
 * Numer wydania z podpowiedziami z `app_release` (TRE-2134). Dotąd był wolnym
 * tekstem przepisywanym ręcznie z /app-config, czyli literówką do zobaczenia
 * przez klientkę.
 *
 * `AutoComplete`, a nie `Select`: numer zapisany we wpisie jest historyczny,
 * a dwa czerwcowe wydania mają numery starsze niż cokolwiek w `app_release` —
 * zamknięta lista odcięłaby możliwość ich wpisania.
 */
const ReleaseVersionField = ({ name, releases }: { name: number; releases: AppRelease[] }) => {
  const form = Form.useFormInstance();
  const platform = Form.useWatch(["releases", name, "platform"], form) as string | undefined;
  const options = useMemo(
    () =>
      releases
        .filter((release) => !platform || release.platform === platform)
        .map((release) => ({ value: release.version })),
    [releases, platform],
  );

  return (
    <Form.Item name={[name, "version"]} rules={[{ required: true }]} noStyle>
      <AutoComplete
        style={{ width: 250 }}
        options={options}
        placeholder="1.1.24 (240)"
        popupMatchSelectWidth={false}
        filterOption={(input, option) =>
          String(option?.value ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />
    </Form.Item>
  );
};

interface HelpArticleSettingsProps {
  open: boolean;
  onClose: () => void;
  kind: HelpArticleKind;
  slugLocked: boolean;
  relatedOptions: { value: string; label: string }[];
  blockers: string[];
}

/**
 * Metadane materiału: wszystko poza tytułem, zajawką i treścią, które żyją
 * wprost na stronie. Ustawienia siedzą w szufladzie, żeby powierzchnia pisania
 * została pusta — redakcja ma widzieć materiał, nie formularz.
 *
 * Komponent renderuje same pola; instancję formularza trzyma strona, bo to ona
 * zapisuje. Kontekst Formu przechodzi przez portal szuflady.
 */
export const HelpArticleSettings = ({
  open,
  onClose,
  kind,
  slugLocked,
  relatedOptions,
  blockers,
}: HelpArticleSettingsProps) => {
  const { releases } = useAppReleases();

  return (
    <Drawer title="Ustawienia materiału" width={520} open={open} onClose={onClose}>
      <Form.Item name="kind" hidden>
        <Input />
      </Form.Item>

      <Form.Item
        label="Adres (slug)"
        name="slug"
        extra={slugLocked ? "Materiał jest opublikowany — adres zostaje bez zmian." : "np. jak-oznaczyc-posilek"}
        rules={[
          { required: true, message: "Adres jest wymagany." },
          { pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/, message: "Dozwolone są małe litery, cyfry i myślniki." },
        ]}
      >
        <Input disabled={slugLocked} />
      </Form.Item>

      <Form.Item label="Kategoria" name="category" rules={[{ required: true }]}>
        <Select options={toOptions(HELP_CATEGORIES)} />
      </Form.Item>

      <Form.Item
        label="Gdzie działa"
        name="platforms"
        rules={[{ required: true, message: "Wybierz co najmniej jedną platformę." }]}
        extra={
          kind === "guide"
            ? "Instrukcje aplikacji są wspólne dla iOS i Androida."
            : "Wpis może rozróżniać iOS i Androida oraz obejmować aplikację i przeglądarkę naraz."
        }
      >
        <Select mode="multiple" options={toOptions(kind === "guide" ? HELP_CLIENT_PLATFORMS : HELP_PLATFORMS)} />
      </Form.Item>

      <Form.Item label="Dla kogo" name="availability" extra="np. „Dla wszystkich klientek z aktywnym pakietem”.">
        <Input />
      </Form.Item>

      <Form.Item
        label="Słowa kluczowe"
        name="keywords"
        extra="Pomagają wyszukiwarce pomocy trafić w to, co klientka wpisze."
      >
        <Select mode="tags" tokenSeparators={[","]} />
      </Form.Item>

      <Form.Item label="Powiązane materiały" name="related">
        <Select mode="multiple" options={relatedOptions} optionFilterProp="label" />
      </Form.Item>

      {kind === "update" && (
        <>
          <Form.Item label="Rodzaj zmiany" name="changeType" rules={[{ required: true }]}>
            <Select options={toOptions(HELP_CHANGE_TYPES)} />
          </Form.Item>

          <Form.Item
            label="Data wydania funkcji"
            name="releasedAt"
            extra="Decyduje o miesiącu w archiwum — nie zmienia jej późniejsza redakcja ani publikacja."
            rules={[{ required: true, message: "Data wydania jest wymagana." }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>

          <Typography.Text strong>Wydania</Typography.Text>
          <Form.List name="releases">
            {(fields, { add, remove }) => (
              <div style={{ marginTop: 8 }}>
                {/* Dwie linie na wydanie, bo szuflada ma 520 px: w jednym rzędzie
                  „Przeglądarka" i „Aktualizacja bez pobierania ze sklepu" nie
                  mieszczą się bez ucinania (TRE-2134). */}
                {fields.map((field) => (
                  <div key={field.key} style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                    <Space align="baseline" style={{ display: "flex" }}>
                      <Form.Item {...field} name={[field.name, "platform"]} rules={[{ required: true }]} noStyle>
                        <Select style={{ width: 140 }} popupMatchSelectWidth={false} options={PLATFORM_OPTIONS} />
                      </Form.Item>
                      <ReleaseVersionField name={field.name} releases={releases} />
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Space>
                    <Form.Item {...field} name={[field.name, "updateType"]} noStyle>
                      <Select
                        style={{ width: "100%" }}
                        popupMatchSelectWidth={false}
                        allowClear
                        placeholder="Sposób aktualizacji"
                        options={UPDATE_TYPE_OPTIONS}
                      />
                    </Form.Item>
                  </div>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ updateType: null })} block>
                  Dodaj wydanie
                </Button>
              </div>
            )}
          </Form.List>
          <Typography.Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
            Numer zapisany we wpisie jest historyczny — późniejsza zmiana konfiguracji aplikacji go nie ruszy. Sposób
            aktualizacji opisuje to wydanie, a nie ścieżkę każdej zainstalowanej wersji.
          </Typography.Paragraph>
        </>
      )}

      {blockers.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message="Do publikacji brakuje:"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          }
        />
      )}
    </Drawer>
  );
};
